import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'

const StyledAccordionRoot = styled(AccordionPrimitive.Root)`
  font-family: Manrope;
  gap: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledAccordionItem = styled(AccordionPrimitive.Item)<{
  $backgroundColor?: string
}>`
  border-radius: 0.25rem;
  padding: 0.2rem;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? $backgroundColor : 'none'};
`

const StyledChevron = styled(ChevronDownIcon)`
  transition: transform 300ms ease-in-out;
  width: 1.5rem;
  height: 2rem;
`

const StyledHeader = styled(AccordionPrimitive.Header)`
  all: unset;
  color: ${({ theme }) => theme.forecast.defaultText};

  height: 45px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  transition-property: color;
  transition-duration: 0.3s;

  &[data-state='open'] ${StyledChevron} {
    transform: rotate(180deg);
  }
`

const StyledTrigger = styled(AccordionPrimitive.Trigger)`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.25rem;
`

const StyledMotionContent = styled(motion.div)`
  color: ${({ theme }) => theme.forecast.text3};
  padding: 0.25rem;
`

export const AccordionRoot = StyledAccordionRoot
export const AccordionItem = StyledAccordionItem
export const AccordionHeader = StyledHeader
export const AccordionTriggerPrimitive = StyledTrigger
export const AccordionChevron = StyledChevron

export type AccordionItem = {
  key: string
  title: React.ReactNode
  content: React.ReactNode
}

/**
 * items are the title and content objects to be rendered in the accordion component
 * openBehavior is either a 'single' drawer can be opened at one time, or multiple drawers can be opened at once
 */
export type AccordionProps = {
  items: Array<AccordionItem>
  backgroundColor?: string
  defaultValue: string[]
}

export const Accordion: React.FC<AccordionProps> = (props) => {
  const { items, backgroundColor, defaultValue } = props
  return (
    <StyledAccordionRoot type={'multiple'} defaultValue={defaultValue ?? []}>
      {items.map((item) => (
        <StyledAccordionItem
          value={item.key}
          key={item.key}
          $backgroundColor={backgroundColor}>
          <AccordionTrigger>{item.title}</AccordionTrigger>
          <AnimatePresence>
            <AccordionContent>{item.content}</AccordionContent>
          </AnimatePresence>
        </StyledAccordionItem>
      ))}
    </StyledAccordionRoot>
  )
}

export const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  { children: React.ReactNode }
>(({ children, ...props }, forwardedRef) => (
  <StyledHeader>
    <StyledTrigger {...props} ref={forwardedRef}>
      {children}
      <StyledChevron aria-hidden />
    </StyledTrigger>
  </StyledHeader>
)) as typeof AccordionPrimitive.Trigger

AccordionTrigger.displayName = 'AccordionTrigger'

export const AccordionContent = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode }
>(({ children, ...props }, forwardedRef) => (
  <AccordionPrimitive.Content ref={forwardedRef} {...props} asChild>
    <StyledMotionContent
      initial={{ y: -10, opacity: 0.2 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10 }}
      transition={{
        type: 'spring',
      }}>
      {children}
    </StyledMotionContent>
  </AccordionPrimitive.Content>
)) as typeof AccordionPrimitive.Content

AccordionContent.displayName = 'AccordionContent'
